import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Badge} from 'primereact/badge'
import {Tag} from 'primereact/tag'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useHistory} from 'react-router-dom/cjs/react-router-dom.min'
import {SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import {getPageUserWithRoles} from '../../healthcare/HealthCareEndPoint'
import {
    useHealthCareCRUD,
    usePageUserWithRoles,
} from '../../healthcare/query-keys/HealthCareQueryManager'
import PageContentWrapper from '../../layout/PageContentWrapper'
import {useRoleContext} from '../../role/RoleProvider'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../broadcast/FilterEvent'
import {
    exportColumnTypes,
    exportRecordTypes,
    exportTypes,
    tableToolbarType,
} from '../Constant'
import {DatatableContext} from '../DataTableContext'
import StyledDataTableWrapper from '../StyledDataTableWrapper'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'
import {
    getHealthCareEventKey,
    healthCareEvent,
    healthCareEventTypes,
    onHealthCareTableChangeRemoveListener,
} from './broadcast/HealthCarePractitionerEvent'
import {hcpColumnList} from './HealthCarePractitionerConstants'
import HealthCarePractitionerForm from './HealthCarePractitionerForm'

const StyledHealthCarePractitionerDatatable = () => {
    useSignals()
    const {handleOnExportDatatable} = useImportNotificationContext()

    const queryClient = useQueryClient()

    //#region Context
    const {user} = useAuth()
    const {userRole, timezoneId} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {hcpManagementPermissions} = useRoleContext()
    const history = useHistory()
    //#endregion

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.hcp,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(tableTypes.hcp)
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
        systemRole: SystemRole.USER_WITH_ROLE,
        hospitalId:
            userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        ...initialAdvancedFilterRequest,
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: null,
            systemRole: SystemRole.USER_WITH_ROLE,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }
    }, [])

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
            systemRole: SystemRole.USER_WITH_ROLE,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }
    }, [])

    // Table Action CRUD
    const formRef = useRef(null)

    const {mutateAsync: deleteHCPractitioner} = useHealthCareCRUD(
        QueryCRUDTypes.delete,
        user?.id,
    )

    const handleAddNew = useCallback(() => {
        formRef.current?.showForm()
    }, [])
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            if (
                userRole === SystemRole.ADMIN ||
                hcpManagementPermissions?.includes(key)
            ) {
                return true
            }

            return false
        },
        [hcpManagementPermissions, userRole],
    )
    //#endregion

    //#region Data Controls
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {healthCareKeys} = QueryKeyManager(QueryKeyTypes.hcp, user?.id)

        return healthCareKeys.page({
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            timezoneId: timezoneId,
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: queryData, isFetched} = usePageUserWithRoles(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        timezoneId,
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )

    const dataList = useRef([])
    const currentDataList = useMemo(() => {
        dataList.current = queryData?.content || []

        return queryData?.content || []
    }, [queryData?.content])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getPageUserWithRoles(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    const handleOnDetailRowClick = id => {
        if (!!id) {
            history.push(`/hcp/${id}`)
        }
    }

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getPageUserWithRoles(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    //#region Event Listener on State Changes
    useEffect(() => {
        // TABLE CHANGE
        healthCareEvent.on(
            `${getHealthCareEventKey(healthCareEventTypes.TABLE_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    userRole === SystemRole.USER_WITH_ROLE &&
                    (!data?.hospitalIds ||
                        (data?.hospitalIds?.length &&
                            !data?.hospitalIds?.includes(currentHospital?.id)))
                ) {
                    return
                }

                // If the HCP table contains a user ID that has been changed.
                if (
                    queryData?.content &&
                    queryData?.content?.length &&
                    !queryData?.content?.includes(data?.id)
                ) {
                    return
                }

                const {healthCareKeys} = QueryKeyManager(
                    QueryKeyTypes.hcp,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: healthCareKeys.pattern(),
                })
            },
        )

        // HCP's ROLE CHANGE
        healthCareEvent.on(
            getHealthCareEventKey(healthCareEventTypes.ROLE_CHANGE),
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.hospitalId ||
                    data?.hospitalId !== currentHospital?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                const {healthCareKeys} = QueryKeyManager(
                    QueryKeyTypes.hcp,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: healthCareKeys.pattern(),
                })
            },
        )

        // PREFECT
        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        // FILTER
        filterDataTableEvent.on(
            `${getFilterEventKey(tableTypes.hcp, filterEventTypes.advanced)}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            onHealthCareTableChangeRemoveListener()
            removeFilterChangeListener(
                tableTypes.hcp,
                filterEventTypes.advanced,
            )
        }
    }, [])
    //#endregion

    //#region Expansion Row
    const isQualifiedForPatientCare = useCallback(role => {
        if (!role || !role?.active || role?.permissions?.length === 0) {
            return false
        }
        let isQualified = false
        if (role?.active && role?.permissions?.length > 0) {
            role.permissions.forEach(rolePermission => {
                if (rolePermission?.qualified) {
                    isQualified = true
                }
            })
        }

        return isQualified
    }, [])

    const rowExpansionTemplate = useCallback(rowData => {
        return (
            <div className="col-12">
                <div className="mb-3">
                    <h6
                        className="underline-60 font-bold"
                        style={{fontSize: '1.2em'}}>
                        {rowData?.fullName}'s Roles by Hospital
                    </h6>
                    <p>
                        The following roles{' '}
                        <span className="font-bold text-darkcyan">
                            ({rowData?.userRoles?.length} in total)
                        </span>{' '}
                        are associated with{' '}
                        <span className="font-bold text-darkcyan">
                            {rowData?.fullName}
                        </span>{' '}
                    </p>
                </div>

                {rowData?.userRoles?.map((child, index) => {
                    const isQualifiedForPatientCareRole =
                        isQualifiedForPatientCare(child)
                    return (
                        <div key={index} className="col-12 cursor-pointer ">
                            <hr className="p-0 m-0" />
                            <div className="flex align-items-center justify-content-around  hover:bg-white py-2 ">
                                <div className="mx-3">
                                    <Badge value={index + 1}></Badge>
                                </div>
                                <div
                                    className="w-full grid"
                                    style={{overflowX: 'auto'}}>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Hospital Number
                                        </h6>
                                        <p>{child?.hospitalNumber || 'N/A'}</p>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Hospital Name
                                        </h6>
                                        <p>{child?.hospitalName || 'N/A'}</p>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Name
                                        </h6>
                                        <p>{child?.roleName || '-'}</p>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Description
                                        </h6>
                                        <div className="flex align-items-center">
                                            <div>
                                                {child?.roleDescription || '-'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Qualified for Taking care of
                                            Patients
                                        </h6>
                                        <div className="flex align-items-center">
                                            <div>
                                                <span>
                                                    {isQualifiedForPatientCareRole ? (
                                                        <Tag
                                                            className="px-3 py-1"
                                                            severity="success"
                                                            value="Yes"></Tag>
                                                    ) : (
                                                        <Tag
                                                            className="px-3 py-1"
                                                            severity="warning"
                                                            value="No"></Tag>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Active
                                        </h6>
                                        <div className="flex align-items-center">
                                            <div>
                                                {!!child?.active ? (
                                                    <Tag
                                                        className="px-3 py-1"
                                                        severity="success"
                                                        value="Yes"></Tag>
                                                ) : (
                                                    <Tag
                                                        className="px-3 py-1"
                                                        severity="danger"
                                                        value="No"></Tag>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Created At
                                        </h6>
                                        <p>{child?.createdAt || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                            <hr className="p-0 m-0" />
                        </div>
                    )
                })}
            </div>
        )
    }, [])
    //#endregion Expansion Row

    //#region Export Data
    const handleRetrieveExportData = async (
        {
            exportType = exportTypes.excel, // csv, excel, pdf
            exportRecordType = exportRecordTypes.allRecords, // all, currentInView
            exportColumns = exportColumnTypes.allColumns, // all, currentInView
        },
        columns,
    ) => {
        handleOnExportDatatable(
            {
                exportType: exportType,
                exportRecordType: exportRecordType,
                exportColumns: exportColumns,
            },
            queryData,
            pageable.value,
            filterRequest.value,
            columns,
            tableTypes.hcp,
        )
    }
    //#endregion

    return (
        <PageContentWrapper>
            <DatatableContext.Provider
                value={{
                    dataKey: 'id',
                    tableType: tableTypes.hcp,
                    queryData: queryData,
                    isFetched: isFetched,
                    columnList: hcpColumnList,
                    tableToolbar: [tableToolbarType.tableControlToolbar],
                    minHeightFullScreen: true,

                    expansionOption: {
                        hasExpansion: true,
                        expansionKey: 'userRoles',
                        rowExpansionTemplate: rowExpansionTemplate,
                    },

                    pageable: pageable.value,
                    onChangeSortColumn: handleChangeSortColumn,
                    onChangePageNumber: handleChangePageNumber,
                    onChangePageRows: handleChangePageRows,
                    onPageRowsHovered: handlePrefetchDataByRowsOption,

                    onDetailRowClick: handleOnDetailRowClick,
                    handleCheckPermissionByKey: handleCheckPermissionByKey,

                    initialAdvancedFilterRequest: initialAdvancedFilterRequest,
                    handleClearFilterRequest: handleClearFilterRequest,
                    handleClearFilterRequestByKey:
                        handleClearFilterRequestByKey,
                    handleClearAdvancedFilterRequest:
                        handleClearAdvancedFilterRequest,
                    handleRetrieveExportData: handleRetrieveExportData,
                    handleChangeGlobalSearch: handleChangeGlobalSearch,

                    handleAddNew: handleAddNew,
                }}>
                <StyledDataTableWrapper
                    headerOptions={{
                        title: 'Medical Staff Members Management',
                        subTitle:
                            'Manage all medical staff members within the system by hospital affiliation.',
                    }}
                />
                <HealthCarePractitionerForm ref={formRef} />
            </DatatableContext.Provider>
        </PageContentWrapper>
    )
}

export default StyledHealthCarePractitionerDatatable
